import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

const ACCESS_KEY = "wap_access";
const REFRESH_KEY = "wap_refresh";

const initialState = {
  user: null,
  accessToken: localStorage.getItem(ACCESS_KEY) || null,
  refreshToken: localStorage.getItem(REFRESH_KEY) || null,
  status: "idle",
  error: null,
};

export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/auth/login", credentials);
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Login failed"));
    }
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Registration failed"));
    }
  }
);

export const fetchMe = createAsyncThunk(
  "auth/me",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/auth/me");
      return data.data.user;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to fetch user"));
    }
  }
);

export const logoutThunk = createAsyncThunk("auth/logout", async () => {
  try {
    await api.post("/auth/logout");
  } catch {}
});

// Update the admin's `preferences` map. Sends a shallow-merge payload —
// pass the FULL inner object for whatever key you're updating (e.g.
// { logsColumns: { ...prevLogsColumns, "2": [...] } }). The server
// shallow-merges at the top level, so any other top-level key in
// preferences (theme, etc.) is preserved.
export const updatePreferences = createAsyncThunk(
  "auth/updatePreferences",
  async (partial, { rejectWithValue }) => {
    try {
      const { data } = await api.patch("/auth/me/preferences", partial);
      return data.data.preferences;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to save preferences"));
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setTokens(state, { payload }) {
      state.accessToken = payload.accessToken;
      state.refreshToken = payload.refreshToken;
      localStorage.setItem(ACCESS_KEY, payload.accessToken);
      localStorage.setItem(REFRESH_KEY, payload.refreshToken);
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const setAuth = (state, { payload }) => {
      state.user = payload.user;
      state.accessToken = payload.accessToken;
      state.refreshToken = payload.refreshToken;
      state.status = "succeeded";
      state.error = null;
      localStorage.setItem(ACCESS_KEY, payload.accessToken);
      localStorage.setItem(REFRESH_KEY, payload.refreshToken);
    };

    builder
      .addCase(login.pending, (s) => {
        s.status = "loading";
        s.error = null;
      })
      .addCase(login.fulfilled, setAuth)
      .addCase(login.rejected, (s, a) => {
        s.status = "failed";
        s.error = a.payload;
      })
      .addCase(register.pending, (s) => {
        s.status = "loading";
        s.error = null;
      })
      .addCase(register.fulfilled, setAuth)
      .addCase(register.rejected, (s, a) => {
        s.status = "failed";
        s.error = a.payload;
      })
      .addCase(fetchMe.fulfilled, (s, a) => {
        s.user = a.payload;
      })
      .addCase(fetchMe.rejected, (s) => {
        s.user = null;
        s.accessToken = null;
        s.refreshToken = null;
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
      })
      .addCase(logoutThunk.fulfilled, (s) => {
        s.user = null;
        s.accessToken = null;
        s.refreshToken = null;
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
      })
      .addCase(updatePreferences.fulfilled, (s, a) => {
        if (s.user) s.user.preferences = a.payload;
      });
  },
});

export const { setTokens, logout, clearError } = authSlice.actions;
export default authSlice.reducer;
